import type { IngestProgress, JobPhase, SyncProgress } from "@noa/api-core";

export type SyncOutcome = "partial" | "complete";

export type SectorCount = { sector: string; count: number };

export type IngestBarStatus = "active" | "exception" | "success" | "normal";

export type RunState = { ended: boolean; incremental: boolean };

/** Documents the ingestion bar counts: the ones gone through during the run, and after it
 * too for an incremental sync; the final counts once an upload has ended. */
export function ingestedSoFar(progress: IngestProgress, { ended, incremental }: RunState): number {
  return ended && !incremental ? progress.completed + progress.skipped : (progress.processed ?? 0);
}

/** How a finished incremental sync left the source; null while it runs, or for other jobs. */
export function syncOutcome(phase: JobPhase, sync: SyncProgress | null | undefined): SyncOutcome | null {
  if (!sync || phase !== "completed") return null;
  return sync.remaining > 0 ? "partial" : "complete";
}

/** Whether a finished sync went through every file left and each one failed: another
 * run would only retry them, so the admin needs the errors first. */
export function onlyFailedFilesLeft(sync: SyncProgress, failed: number): boolean {
  return sync.remaining > 0 && sync.remaining <= failed;
}

/** The sectors as the sync processes them, named by their number ("1000 - " → "1000"). */
export function sectorCounts(pendingBySector: Record<string, number>): SectorCount[] {
  return (
    Object.entries(pendingBySector)
      .map(([prefix, count]) => ({ sector: prefix.replace(/[\s-]+$/, ""), count }))
      // The empty prefix is the whole source, as for PEAs found anywhere in the drive
      .filter(({ sector }) => sector !== "")
  );
}

/** The bar is a success only when every file went through and none failed. */
export function ingestBarStatus(phase: JobPhase, percent: number, failed: number): IngestBarStatus {
  if (phase === "failed") return "exception";
  if (percent >= 100 && failed === 0) return "success";
  return phase === "completed" ? "normal" : "active";
}
